// useCurrentProgramme — qué programa está en antena, y cuándo dejará de estarlo.
//
// Lo compartían Home y Radio copiando el mismo bloque, y con él dos fallos:
//
// 🔴 **Un hueco de parrilla no limpiaba el programa.** Las dos vistas hacían
// `if (now) currentShow.value = now`, así que cuando `getCurrentShow` devolvía
// `null` —que es lo que devuelve, legítimamente, en una franja sin programa— se
// quedaba el anterior. El nombre y la imagen de fondo seguían anunciando un
// programa que ya no estaba en antena, y no había forma de que se corrigiera.
// Los fallos de red van por el `catch`, así que un `null` significa «ahora no
// hay programa» y hay que hacerle caso.
//
// 🔴 **El cambio de franja tardaba hasta cinco minutos.** El único refresco era
// un intervalo fijo. Un programa que entra a las 16:00 no aparecía hasta las
// 16:05, con el fondo y la ficha del anterior.
//
// Aquí el refresco se programa **para el final de la franja**: se sabe cuándo
// termina, así que se relee entonces y no antes. El intervalo largo se queda
// como red de seguridad —pestaña dormida, cambio de hora, parrilla editada en
// caliente— y no como mecanismo principal.
import { ref, computed, onUnmounted } from "vue";
import { getCurrentShow } from "@/services/currentShow";
import { getNextShow } from "@/services/nextShow";
import { getRadioCurrentTime } from "@/utils/radioTime";

// Red de seguridad. No es lo que hace el trabajo.
const RED_DE_SEGURIDAD_MS = 5 * 60 * 1000;

// Margen tras el fin de la franja: el reloj del navegador y el de la radio no
// tienen por qué coincidir al segundo, y releer un instante antes devolvería
// otra vez el programa que acaba de terminar.
const MARGEN_MS = 4000;

export function useCurrentProgramme(stationId = "3", { withNext = false } = {}) {
  const currentShow = ref(null);
  const nextShow = ref(null);

  const programme = computed(() => currentShow.value?.name || "Astra FM");

  let redTimer = null;
  let franjaTimer = null;

  function limpiarFranja() {
    if (franjaTimer) clearTimeout(franjaTimer);
    franjaTimer = null;
  }

  // Cuánto queda para que termine lo que está sonando. En horas decimales, con
  // el salto de medianoche resuelto: un programa de 22 a 2 termina «mañana».
  function msHastaElFinal() {
    const fin = currentShow.value?.endTime;
    if (fin == null) return null;
    const ahora = getRadioCurrentTime();
    const horas = fin > ahora ? fin - ahora : fin + 24 - ahora;
    return horas * 3600 * 1000 + MARGEN_MS;
  }

  async function load() {
    try {
      // Desde el 26.08.26 la programación sale del servidor de la radio y ya no
      // hay parrilla por estación: `stationId` sobrevive en la firma para no
      // tocar a quien llama, y los servicios lo ignoran. Con él se fue la
      // trampa del índice —`'3' ? 1 : 0`— que elegía parrilla por posición
      // dentro del array de WordPress y podía anunciar otro programa sin dar
      // ningún error.
      const [now, next] = await Promise.all([
        getCurrentShow(null, stationId),
        withNext ? getNextShow(null, stationId) : Promise.resolve(null),
      ]);

      // Se asigna SIEMPRE, también `null`: es la corrección del fallo de
      // arriba. Un hueco de parrilla tiene que verse como un hueco.
      currentShow.value = now || null;
      if (withNext) nextShow.value = next || null;
    } catch (error) {
      // Un fallo de red NO limpia lo que ya se sabía: es mejor un programa de
      // hace cinco minutos que ninguno. Es la diferencia con el `null` de
      // arriba, que sí es un dato.
    } finally {
      programarSiguiente();
    }
  }

  function programarSiguiente() {
    limpiarFranja();
    const ms = msHastaElFinal();
    // Sin fin conocido —hueco de parrilla— manda la red de seguridad.
    if (ms == null || ms <= 0) return;
    // `setTimeout` no admite plazos mayores de ~24,8 días, y aquí nunca pasan
    // de un día; aun así se acota por si una franja llega mal formada.
    franjaTimer = setTimeout(load, Math.min(ms, RED_DE_SEGURIDAD_MS * 12));
  }

  function start() {
    stop();
    load();
    redTimer = setInterval(load, RED_DE_SEGURIDAD_MS);
  }

  function stop() {
    if (redTimer) clearInterval(redTimer);
    redTimer = null;
    limpiarFranja();
  }

  onUnmounted(stop);

  return { currentShow, nextShow, programme, load, start, stop };
}
